import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Image as ImageIcon, Megaphone, Store, Loader2, Search, FileText, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLabsStoreConfigPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('super_admin_token');
  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [featured, setFeatured] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [banners, setBanners] = useState([]);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerMeta, setBannerMeta] = useState({ title: '', link_url: '' });
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const [popup, setPopup] = useState({ image_url: '', link_url: '', title: '', is_active: false, file_type: 'image' });
  const [popupFile, setPopupFile] = useState(null);
  const [popupActive, setPopupActive] = useState(false);
  const [uploadingPopup, setUploadingPopup] = useState(false);

  const fetchFeatured = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/labs-store/admin/featured`, { headers });
    if (res.ok) setFeatured(await res.json());
  }, []);

  const fetchBanners = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/labs-store/admin/banners`, { headers });
    if (res.ok) setBanners(await res.json());
  }, []);

  const fetchPopup = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/labs-store/admin/popup`, { headers });
    if (res.ok) {
      const data = await res.json();
      setPopup(data);
      setPopupActive(!!data.is_active);
    }
  }, []);

  useEffect(() => { fetchFeatured(); fetchBanners(); fetchPopup(); }, [fetchFeatured, fetchBanners, fetchPopup]);

  // Debounced cross-vendor item search
  useEffect(() => {
    if (searchQuery.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      fetch(`${API_URL}/api/labs-store/admin/search-items?q=${encodeURIComponent(searchQuery)}`, { headers })
        .then((res) => res.json())
        .then((data) => setSearchResults(data))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const toggleSelect = (item) => {
    const key = `${item.vendor_id}_${item.item_id}`;
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = item;
      return next;
    });
  };

  const selectedCount = Object.keys(selectedIds).length;

  const addSelectedToStore = async () => {
    if (selectedCount === 0) return;
    setSubmitting(true);
    try {
      const items = Object.values(selectedIds).map((i) => ({ vendor_id: i.vendor_id, item_id: i.item_id, item_type: i.item_type }));
      const res = await fetch(`${API_URL}/api/labs-store/admin/featured/bulk`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify({ items, audience: 'doctor' }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${data.added} item(s) added${data.skipped ? `, ${data.skipped} already featured` : ''}`);
        setSelectedIds({});
        setSearchQuery('');
        setSearchResults([]);
        fetchFeatured();
      } else {
        toast.error(data.detail || 'Failed to add items');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const removeFeatured = async (id) => {
    const res = await fetch(`${API_URL}/api/labs-store/admin/featured/${id}`, { method: 'DELETE', headers });
    if (res.ok) { toast.success('Removed'); fetchFeatured(); }
  };

  const toggleFeaturedActive = async (item) => {
    const res = await fetch(`${API_URL}/api/labs-store/admin/featured/${item.id}`, {
      method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ is_active: !item.is_active }),
    });
    if (res.ok) fetchFeatured();
  };

  const uploadBanner = async () => {
    if (!bannerFile) { toast.error('Please choose a PNG, JPEG, or PDF file'); return; }
    setUploadingBanner(true);
    try {
      const form = new FormData();
      form.append('file', bannerFile);
      form.append('title', bannerMeta.title);
      form.append('link_url', bannerMeta.link_url);
      const res = await fetch(`${API_URL}/api/labs-store/admin/banners/upload`, { method: 'POST', headers, body: form });
      const data = await res.json();
      if (res.ok) {
        toast.success('Banner uploaded');
        setBannerFile(null);
        setBannerMeta({ title: '', link_url: '' });
        fetchBanners();
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } finally {
      setUploadingBanner(false);
    }
  };

  const deleteBanner = async (id) => {
    const res = await fetch(`${API_URL}/api/labs-store/admin/banners/${id}`, { method: 'DELETE', headers });
    if (res.ok) { toast.success('Banner removed'); fetchBanners(); }
  };

  const uploadPopup = async () => {
    if (!popupFile) { toast.error('Please choose a PNG, JPEG, or PDF file'); return; }
    setUploadingPopup(true);
    try {
      const form = new FormData();
      form.append('file', popupFile);
      form.append('title', popup.title || '');
      form.append('link_url', popup.link_url || '');
      form.append('is_active', String(popupActive));
      const res = await fetch(`${API_URL}/api/labs-store/admin/popup/upload`, { method: 'PUT', headers, body: form });
      const data = await res.json();
      if (res.ok) {
        toast.success('Popup saved');
        setPopup(data);
        setPopupFile(null);
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } finally {
      setUploadingPopup(false);
    }
  };

  const savePopupMeta = async () => {
    const res = await fetch(`${API_URL}/api/labs-store/admin/popup`, {
      method: 'PUT', headers: jsonHeaders,
      body: JSON.stringify({ ...popup, is_active: popupActive }),
    });
    if (res.ok) toast.success('Popup settings saved');
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6" data-testid="admin-labs-store-config-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')} data-testid="admin-labs-store-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Labs & Store Configuration</h1>
            <p className="text-sm text-slate-500">Curate featured products, banners, and ad popup shown to doctors</p>
          </div>
        </div>

        <Tabs defaultValue="featured">
          <TabsList>
            <TabsTrigger value="featured" data-testid="admin-labs-store-tab-featured"><Store className="w-4 h-4 mr-1.5" /> Featured Items</TabsTrigger>
            <TabsTrigger value="banners" data-testid="admin-labs-store-tab-banners"><ImageIcon className="w-4 h-4 mr-1.5" /> Banners</TabsTrigger>
            <TabsTrigger value="popup" data-testid="admin-labs-store-tab-popup"><Megaphone className="w-4 h-4 mr-1.5" /> Ad Popup</TabsTrigger>
          </TabsList>

          <TabsContent value="featured" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Search & Feature Items</CardTitle>
                <CardDescription>Search across ALL approved vendors' catalogs, select multiple items, and add them all at once</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    className="pl-9"
                    placeholder="Type a test/product/service name (min 2 characters)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="admin-labs-store-search-input"
                  />
                </div>

                {searching ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-teal-600" /></div>
                ) : searchResults.length > 0 ? (
                  <div className="space-y-1.5 max-h-80 overflow-y-auto border rounded-lg p-2">
                    {searchResults.map((item) => {
                      const key = `${item.vendor_id}_${item.item_id}`;
                      return (
                        <label key={key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`admin-labs-store-search-result-${key}`}>
                          <Checkbox checked={!!selectedIds[key]} onCheckedChange={() => toggleSelect(item)} />
                          <div className="flex-1">
                            <p className="text-sm font-medium">{item.name}</p>
                            <p className="text-xs text-slate-400">{item.vendor_name} ({item.vendor_type}) • MRP ₹{item.mrp} • Patient MRP ₹{item.patient_mrp} • B2B ₹{item.b2b_price}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : searchQuery.trim().length >= 2 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No matching items found.</p>
                ) : null}

                {selectedCount > 0 && (
                  <Button onClick={addSelectedToStore} disabled={submitting} data-testid="admin-labs-store-bulk-add-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add {selectedCount} Selected Item{selectedCount > 1 ? 's' : ''} to Store
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Currently Featured ({featured.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {featured.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No items featured yet. Use the search above to add some.</p> : featured.map((f) => (
                  <div key={f.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`admin-labs-store-featured-row-${f.id}`}>
                    <div>
                      <p className="font-medium text-sm">{f.item_details?.name || 'Unknown item'}</p>
                      <p className="text-xs text-slate-400">{f.vendor_name} • MRP ₹{f.item_details?.mrp} • Patient MRP ₹{f.item_details?.patient_mrp} • B2B ₹{f.item_details?.b2b_price}</p>
                      {!f.is_active && <Badge variant="secondary" className="mt-1 text-xs">Inactive</Badge>}
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch checked={f.is_active} onCheckedChange={() => toggleFeaturedActive(f)} data-testid={`admin-labs-store-toggle-${f.id}`} />
                      <Button variant="ghost" size="icon" onClick={() => removeFeatured(f.id)} data-testid={`admin-labs-store-remove-${f.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="banners" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Upload Banner</CardTitle>
                <CardDescription>Recommended size: 1200 x 400 px (3:1 ratio) • PNG, JPEG or PDF, max 8MB</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>File</Label>
                  <Input type="file" accept="image/png,image/jpeg,application/pdf" onChange={(e) => setBannerFile(e.target.files?.[0] || null)} data-testid="admin-labs-store-banner-file" />
                </div>
                <div><Label>Link URL (optional)</Label><Input value={bannerMeta.link_url} onChange={(e) => setBannerMeta({ ...bannerMeta, link_url: e.target.value })} data-testid="admin-labs-store-banner-link" /></div>
                <div><Label>Title (optional)</Label><Input value={bannerMeta.title} onChange={(e) => setBannerMeta({ ...bannerMeta, title: e.target.value })} data-testid="admin-labs-store-banner-title" /></div>
                <Button onClick={uploadBanner} disabled={uploadingBanner} data-testid="admin-labs-store-add-banner-btn">
                  {uploadingBanner ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Upload Banner
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Banners ({banners.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {banners.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No banners added yet.</p> : banners.map((b) => (
                  <div key={b.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`admin-labs-store-banner-row-${b.id}`}>
                    <div className="flex items-center gap-3">
                      {b.file_type === 'pdf' ? (
                        <div className="w-16 h-10 bg-slate-100 rounded flex items-center justify-center"><FileText className="w-5 h-5 text-slate-400" /></div>
                      ) : (
                        <img src={`${API_URL}${b.image_url}`} alt="" className="w-16 h-10 object-cover rounded" />
                      )}
                      <p className="text-sm font-medium">{b.title || b.image_url}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => deleteBanner(b.id)} data-testid={`admin-labs-store-delete-banner-${b.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="popup" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Ad Popup</CardTitle>
                <CardDescription>Shown once per session on the Labs & Store page. Recommended size: 600 x 800 px (3:4 ratio) • PNG, JPEG or PDF, max 8MB</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {popup.image_url && (
                  <div className="flex items-center gap-3 p-2 border rounded-lg">
                    {popup.file_type === 'pdf' ? (
                      <div className="w-16 h-20 bg-slate-100 rounded flex items-center justify-center"><FileText className="w-5 h-5 text-slate-400" /></div>
                    ) : (
                      <img src={`${API_URL}${popup.image_url}`} alt="" className="w-16 h-20 object-cover rounded" />
                    )}
                    <p className="text-sm text-slate-500">Current popup file</p>
                  </div>
                )}
                <div>
                  <Label>Replace File</Label>
                  <Input type="file" accept="image/png,image/jpeg,application/pdf" onChange={(e) => setPopupFile(e.target.files?.[0] || null)} data-testid="admin-labs-store-popup-file" />
                </div>
                <div><Label>Link URL</Label><Input value={popup.link_url || ''} onChange={(e) => setPopup({ ...popup, link_url: e.target.value })} data-testid="admin-labs-store-popup-link" /></div>
                <div><Label>Title</Label><Input value={popup.title || ''} onChange={(e) => setPopup({ ...popup, title: e.target.value })} data-testid="admin-labs-store-popup-title" /></div>
                <div className="flex items-center gap-2">
                  <Switch checked={popupActive} onCheckedChange={setPopupActive} data-testid="admin-labs-store-popup-active" />
                  <Label>Active</Label>
                </div>
                <div className="flex gap-2">
                  <Button onClick={uploadPopup} disabled={uploadingPopup} data-testid="admin-labs-store-popup-upload-btn">
                    {uploadingPopup ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Upload New File
                  </Button>
                  <Button variant="outline" onClick={savePopupMeta} data-testid="admin-labs-store-popup-save-btn">Save Title/Link/Active Only</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
